import React, { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import { compact, first, get, isEmpty } from 'lodash'
import compose from 'lodash/fp/compose'
import memoize from 'moize'

import {
  Router as HashRouter,
  Switch,
  Route,
  Redirect,
  matchPath
} from 'react-router-dom'

import { withTranslation } from 'react-i18next'

import Shell from '@ublique/components/Shell'
import NotFound from '@ublique/components/PageNotFound'
import Forbidden from '@ublique/components/PageForbidden'

import { withStoreCurried } from 'hoc/withStore'
import { getI18nControls } from 'utils/hooks/useI18nControls'
import NotificationPanel from 'components/NotificationPanel'
import history from 'services/history'
import { canReadResource, canResource } from 'services/auth'
import { getUserRoles, SUPER_USER_ROLE } from 'services/auth/isUserAuthorized'
import ModalHandler from 'components/Modal'
import ErrorsHandler from 'components/ErrorsHandler'
import NotificationsHandler from 'components/Notifications'
import { AUTH_ACCESS } from 'config'

import Breadcrumbs from './Breadcrumbs'
import {
  flattenRoutes,
  matchPathRecursive,
  inheritParentRouteProps,
  mapRoutesToHeaders,
  getSidebarListTables,
  getSidebarTitleProp,
  getExtraProps,
  getInstanceActions,
  getTranslationsActions,
  getCurrentRoute,
  getFilterProps,
  getUserInfo
} from './utils'
import Settings from 'components/Settings'
import { defaultPermission } from 'pages/Scenario/state'
import { EXTRA_OUTPUT, UBLIQUE } from 'src/App/constants'
import { GENERAL_CONFIG } from 'pages/InputView/constants'
import getSidebar from 'utils/sidebar/getSidebar'

const hidden = { display: 'none' }

const extraHeaderActionComponents = [
  <NotificationPanel key='notification-panel' />
]
const settingsPanelActionComponents = (
  librariesVersion,
  urlChangelogComponents,
  manualsSettings,
  productChangelogUrl
) => {
  return [
    <Settings
      key='settings-panel'
      librariesVersion={librariesVersion}
      urlChangelogComponents={urlChangelogComponents}
      manualsSettings={manualsSettings}
      productChangelogUrl={productChangelogUrl}
    />
  ]
}

// Use the current language (first argument) as a key, since the entire i18n object does not change
const memoizedGetI18nControls = memoize(
  (_, ...rest) => getI18nControls(...rest),
  { maxArgs: 1 }
)

const getHeaderList = memoize(
  (
    routes,
    instances,
    scenario,
    scenarioManagerPermission,
    extraOutputs = [],
    t,
    extraOutputT
  ) => {
    const headerItems = mapRoutesToHeaders(routes, t)

    const extraOutItemIndex = headerItems.findIndex(
      (item) => item.id === EXTRA_OUTPUT
    )

    const extraOutputParent = headerItems[extraOutItemIndex] || {}
    const extraOutputItems = []

    extraOutputs?.forEach((extraOutput) => {
      const item = {}

      if (!extraOutput.headerGroup) {
        // Here we need to create single header link if headerGroup prop is not specify for this route
        item.title = extraOutputT(extraOutput.title)
        item.href = `${extraOutputParent.path}${extraOutput.path}`
        item.items = null
      } else {
        // Here we group routes by headerGroup prop

        const isGroupAlreadyIn = extraOutputItems.find(
          (item) => item.title === extraOutput.headerGroup
        )

        if (!isGroupAlreadyIn) {
          item.title = extraOutput.headerGroup
          const iteratedGroup = extraOutputs.filter(
            ({ headerGroup }) => headerGroup === extraOutput.headerGroup
          )

          item.items = iteratedGroup.map((route) => ({
            ...route,
            headerGroup: extraOutputT(route.headerGroup),
            href: `${extraOutputParent.path}${route.path}`,
            title: extraOutputT(route?.title)
          }))
          item.onlyGroupLink = extraOutput.onlyGroupLink
          item.headerGroup = extraOutputT(extraOutput.headerGroup)
        }
      }

      !isEmpty(item) && extraOutputItems.push(item)
    })

    headerItems.splice(extraOutItemIndex, 1)

    // Instance handling
    const instancesItemIndex = headerItems.findIndex(
      (item) => item.id === 'instances'
    )

    const instancesSubitems = instances.filter((i) => i.config !== 'scenario')

    const instancesItem = isEmpty(instancesSubitems)
      ? null // Hide instances menu when it has no children
      : { ...headerItems[instancesItemIndex], items: instancesSubitems }

    headerItems.splice(instancesItemIndex, 1)
    // Scenario manager button handling
    // permissions
    const {
      permissions: { owner, others }
    } = scenario

    const scenarioItemIndex = headerItems.findIndex(
      (item) => item.id === 'scenario'
    )

    const scenarioItem =
      !scenarioManagerPermission.read || (!owner && !others)
        ? null
        : headerItems[scenarioItemIndex]

    headerItems.splice(scenarioItemIndex, 1)

    return compact([
      instancesItem,
      scenarioItem,
      ...extraOutputItems,
      ...headerItems
    ]).filter((route) => canReadResource(route.id))
  }
)

class Router extends PureComponent {
  constructor(props) {
    super(props)

    const routes = inheritParentRouteProps(props.routes)
    const flatRoutes = flattenRoutes(routes)
    this.state = {
      currentRoute: null,
      routes,
      flatRoutes,
      urlChangelogComponents: '',
      productChangelogUrl: '',
      mapModelByOrder: []
    }
  }

  componentDidMount() {
    this.setCurrentRoute()
    // Get pathname from history.location instead of listen callback params.
    // Browser PUSH/POP do not sync correctly with the history listener.
    this.unlistenRouteChange = history.listen(this.setCurrentRoute)
  }

  componentWillUnmount() {
    this.unlistenRouteChange()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.routes !== this.props.routes) {
      const { pathname } = history.location
      const routes = inheritParentRouteProps(this.props.routes)
      const flatRoutes = flattenRoutes(routes)
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        routes,
        flatRoutes,
        currentRoute: getCurrentRoute(pathname, { flatRoutes, routes })
      })
    }
  }

  setCurrentRoute = () => {
    const { pathname } = history.location
    const { flatRoutes, routes } = this.state
    this.setState({
      currentRoute: getCurrentRoute(pathname, { flatRoutes, routes })
    })
  }

  routeRenderer = memoize(
    (route) => {
      return (props) => {
        const {
          location: { pathname }
        } = props

        const { checkAuthorization, t } = this.props
        const { component: Component, extraProps, mode = 'stacked' } = route
        const permissions = canResource(route.id)

        if (checkAuthorization) {
          const isAuthorized = checkAuthorization(route)

          if (isAuthorized === AUTH_ACCESS.FORBIDDEN) {
            return (
              <Forbidden
                goBackText={t('Common:GoBack')}
                text={t('Common:PageForbidden')}
              />
            )
          }
          if (isAuthorized === AUTH_ACCESS.UNAUTHORIZED) {
            return <Redirect to='/' />
          }
        }

        if (!matchPathRecursive(pathname, route)) {
          return (
            <NotFound
              goBackText={t('Common:GoBack')}
              text={t('Common:PageNotFound')}
            />
          )
        }

        const routeComponentProps = Object.assign(
          {},
          props,
          getExtraProps(this.props, extraProps), // Pass extra props to the component, taken from the router props
          permissions,
          { route }
        )

        if (route.routes) {
          const isCurrent = !!matchPath(pathname, {
            path: route.path,
            exact: true
          })

          if (mode === 'none') {
            return (
              <Fragment>
                {this.renderBreadcrumbs}
                <Component {...routeComponentProps} />
              </Fragment>
            )
          } else if (mode === 'stacked') {
            return (
              <Fragment>
                <div style={isCurrent ? undefined : hidden}>
                  {this.renderBreadcrumbs}
                  <Component {...routeComponentProps} />
                </div>
                <Switch>{route.routes.map(this.renderRoute)}</Switch>
              </Fragment>
            )
          } else if (mode === 'nested') {
            return (
              <Component {...routeComponentProps}>
                <Switch>
                  {route.routes.map(this.renderRoute)}
                  <Redirect to={first(route.routes).path} />
                </Switch>
              </Component>
            )
          } else {
            console.error('Invalid child routes mode')
          }
        }

        return (
          <Fragment>
            {this.renderBreadcrumbs}
            <Component {...routeComponentProps} />
          </Fragment>
        )
      }
    },
    {
      isReact: true,
      transformArgs: ([route]) => {
        return route.id || route.path || route.title
      }
    }
  )

  renderRoute = (route, index) => {
    const { path, component, routes } = route
    const { t } = this.props
    const {
      location: { pathname }
    } = history
    if (!component && routes) {
      return routes.map(this.renderRoute)
    }

    const isCurrent = matchPath(pathname, {
      path: route.path,
      exact: false
    })

    if (isCurrent && route?.path !== '/') {
      if (route.title === 'TBD') {
        let title = UBLIQUE
        const tPrefix = 'OutputView:SideNavigation.'

        if (isCurrent.params[route.titleParam] && !route.isDetail) {
          title = `${UBLIQUE} | ${t(
            tPrefix + isCurrent?.params[route.titleParam]?.toUpperCase()
          )}`
        }

        if (route.isDetail) {
          title = `${UBLIQUE} | ${t(tPrefix + route.detailTitle)}`
        }

        document.title = title
      } else {
        const tPrefix = 'Shell:'
        document.title = route?.title
          ? `${UBLIQUE} | ${t(tPrefix + route.title)}`
          : UBLIQUE
      }
    }

    return (
      <Route
        {...route}
        path={path}
        component={this.routeRenderer(route)}
        key={
          isCurrent
            ? isCurrent.url
            : route.id || route.path || route.title || index
        }
      />
    )
  }

  get i18nControls() {
    const { i18n, languagesMenuConfig } = this.props
    return memoizedGetI18nControls(i18n.language, i18n, languagesMenuConfig)
  }

  get shellProps() {
    const {
      t,
      extraOutputT,
      logo,
      instances,
      scenario,
      userActionMenu,
      userActionAvatar,
      sidebarIcon,
      sidebarList,
      filter,
      user,
      settingsLabels,
      librariesVersion,
      manualsSettings,
      appConfig,
      productChangelogUrl
    } = this.props

    const { currentRoute, routes, urlChangelogComponents, mapModelByOrder } =
      this.state

    const { outputConfigRoutes, models } = appConfig
    const modelConfig = models[GENERAL_CONFIG] || {}

    const { exportAllowed } = modelConfig

    const safeUser = user || {}
    const userInfo = getUserInfo(user)
    const filterProps = getFilterProps(filter)
    const isSuperuser = getUserRoles(user)?.includes(SUPER_USER_ROLE)
    const {
      shell = true,
      filterBar = false,
      contentPadding = true,
      expandSidebar,
      contentWrapperClassname,
      showSidebarSearch = true
    } = currentRoute || {}

    const sidebar = getSidebar(currentRoute || {})

    const instancePath = matchPath(history.location.pathname, {
      path: '/instances/:instanceId/workingtables',
      exact: false
    })
    const currentInstanceId = instancePath?.params?.instanceId
    const instancePermission = currentInstanceId
      ? (instances.find(({ id }) => id == currentInstanceId) || {}).permission // eslint-disable-line eqeqeq
      : undefined

    const outputPath = matchPath(history.location.pathname, {
      path: '/output/:scenarioId',
      exact: false
    })
    const scenarioId = outputPath?.params?.scenarioId

    const itemsList = sidebarList.items?.map((wt) => {
      if (wt.loading || !sidebarIcon) return wt
      else
        return {
          ...wt,
          icon: instancePath ? sidebarIcon : wt.icon
        }
    })

    const versions = { platformVersion: appConfig.appVersion }
    const scenarioManagerPermission = get(
      appConfig,
      'scenarioModelConfig.scenario.permissions',
      defaultPermission
    )
    const getChangelogComponents = (url) => {
      this.setState({ urlChangelogComponents: url })
    }

    const getMapModelByDragAndDrop = (mappa) => {
      if (sidebarList?.drag) {
        this.setState({ mapModelByOrder: mappa })
      }
    }

    const outputConfig = get(appConfig, 'outputConfigRoutes')
    // const outputList = get(appConfig, 'outputConfigRoutes.children')
    const extraOutputs = outputConfig?.children?.filter(
      (route) => route?.type === 'extraOutput'
    )
    const getShowSidebar = () => {
      if (currentRoute?.id === 'extraOutputView' && shell) {
        const path = history.location.pathname.split('/')
        const extraOutputPath = path.pop()

        return (
          extraOutputs?.find((route) => {
            return extraOutputPath === route.component
          })?.extraOutputSidebar || false
        )
      } else {
        return shell && sidebar
      }
    }

    return {
      showHeader: shell,
      showSidebar: getShowSidebar(),
      showFilterbar: shell && filterBar !== false,
      searchPlaceholder: t('SearchPlaceholder'),
      showSidebarSearch,
      headerList: getHeaderList(
        routes,
        instances,
        scenario,
        scenarioManagerPermission,
        extraOutputs,
        t,
        extraOutputT
      ),
      sidebarList: getSidebarListTables(
        itemsList,
        sidebarList,
        instances,
        currentInstanceId,
        exportAllowed,
        t,
        models
      ),
      sidebarTitleProp: getSidebarTitleProp(
        sidebarList,
        instances,
        outputConfigRoutes?.scenario,
        currentInstanceId,
        scenarioId,
        t
      ),
      sidebarActions:
        sidebarList.type === 'translations'
          ? getTranslationsActions(t)
          : getInstanceActions(
              currentInstanceId,
              instancePermission,
              isSuperuser,
              sidebarList,
              instances,
              mapModelByOrder,
              history,
              exportAllowed,
              t
            ),
      enableDragAndDrop:
        sidebarList.type === 'workingTables' && sidebarList.drag,
      contentClassname: contentWrapperClassname,
      userActionList: userActionMenu,
      userActionAvatar: userActionAvatar,
      userInfo: userInfo,
      i18nControls: this.i18nControls,
      versions,
      settingsLabels,
      settingPanelContent: settingsPanelActionComponents(
        librariesVersion,
        urlChangelogComponents,
        manualsSettings,
        productChangelogUrl
      ),
      location: history.location.pathname,
      contentPadding,
      expandSidebar,
      filterProps,
      extraHeaderActionComponents,
      productLogo: logo,
      titleLink: instancePath?.url,
      userName: safeUser.given_name || safeUser.sub,
      userSurname: safeUser.family_name,
      getChangelogComponents,
      getMapModelByDragAndDrop
      // userAvatarSrc: safeUser.avatarSrc
    }
  }

  get renderBreadcrumbs() {
    const { t, instances, sidebarList } = this.props
    const { currentRoute } = this.state

    const {
      shell = true,
      breadcrumbs = true,
      breadcrumbsType
    } = currentRoute || {}

    return shell && breadcrumbs ? (
      <Breadcrumbs
        t={t}
        currentRoute={currentRoute}
        type={breadcrumbsType}
        instances={instances}
        items={sidebarList.items}
      />
    ) : null
  }

  render() {
    const { modals, children } = this.props
    const { routes } = this.state
    return (
      <div>
        <HashRouter history={history}>
          <Shell {...this.shellProps}>
            <Switch>{routes.map(this.renderRoute)}</Switch>
            {children}
          </Shell>
          <ModalHandler modals={modals} />
          <ErrorsHandler />
          <NotificationsHandler />
        </HashRouter>
      </div>
    )
  }
}

const PropTypesRouteShape = {
  path: PropTypes.string,
  component: PropTypes.elementType,
  title: PropTypes.string,
  shell: PropTypes.bool,
  public: PropTypes.bool,
  header: PropTypes.bool,
  breadcrumbs: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  contentPadding: PropTypes.bool,
  expandSidebar: PropTypes.bool,
  contentWrapperClassname: PropTypes.string
}

const PropTypesRoutes = PropTypes.arrayOf(PropTypes.shape(PropTypesRouteShape))

PropTypesRouteShape.routes = PropTypesRoutes

Router.propTypes = {
  checkAuthorization: PropTypes.func,
  routes: PropTypesRoutes,
  children: PropTypes.node
}

const subscriptions = [
  'instances',
  'filter',
  'sidebarList',
  'user',
  'scenario',
  'appConfig'
]
const i18nNamespaces = ['Shell', 'Sidebar', 'Common']

export default compose(
  React.memo,
  withTranslation(i18nNamespaces),
  withStoreCurried(subscriptions)
)(Router)
